import React, { useEffect, useRef, useState } from "react";
import { useLanguage } from "../context/LanguageContext";
import { motion, AnimatePresence, cubicBezier } from "framer-motion";

/* ----------------- Utility Hooks ----------------- */
function useReveal() {
    const ref = useRef(null);
    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === "undefined") return;
        node.classList.add(
            "opacity-0",
            "translate-y-6",
            "transition-opacity",
            "transition-transform",
            "duration-500",
            "ease-out"
        );
        const io = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        node.classList.remove("opacity-0", "translate-y-6");
                        node.classList.add("opacity-100", "translate-y-0");
                        io.unobserve(node);
                    }
                });
            },
            { threshold: 0.12 }
        );
        io.observe(node);
        return () => io.disconnect();
    }, []);
    return ref;
}

function useIdleHydration(delayMs = 150) {
    const [ready, setReady] = useState(() => typeof window === "undefined");
    useEffect(() => {
        if (ready) return;
        if (typeof window === "undefined") {
            setReady(true);
            return;
        }
        let cancelled = false;
        const start = () => {
            if (!cancelled) setReady(true);
        };
        const timeoutId = window.setTimeout(start, Math.max(delayMs, 50));
        return () => {
            cancelled = true;
            window.clearTimeout(timeoutId);
        };
    }, [delayMs, ready]);
    return ready;
}

/* ----------------- Scaling Utilities ----------------- */
function useFitScale(baseW, baseH) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const apply = (w) => {
            const scale = Math.min(1, w / baseW);
            el.style.setProperty("--bw", `${baseW}px`);
            el.style.setProperty("--bh", `${baseH}px`);
            el.style.setProperty("--scale", `${scale}`);
            el.style.setProperty("--outer-h", `${Math.round(baseH * scale)}px`);
        };
        const ro = new ResizeObserver((entries) => {
            for (const e of entries) apply(e.contentRect.width);
        });
        ro.observe(el);
        apply(el.getBoundingClientRect().width);
        return () => ro.disconnect();
    }, [baseW, baseH]);
    return ref;
}

function FitScale({ baseW, baseH, className, children }) {
    const ref = useFitScale(baseW, baseH);
    return (
        <div
            ref={ref}
            className={className}
            style={{ width: "100%", maxWidth: "720px", height: "var(--outer-h)" }}
        >
            <div
                style={{
                    width: "var(--bw)",
                    height: "var(--bh)",
                    transform: "scale(var(--scale))",
                    transformOrigin: "top left",
                    willChange: "transform"
                }}
            >
                {children}
            </div>
        </div>
    );
}

function useAreaScale(baseW, baseH) {
    const ref = useRef(null);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const apply = (w) => {
            const s = Math.min(1, w / baseW);
            el.style.setProperty("--gw", `${baseW}px`);
            el.style.setProperty("--gh", `${baseH}px`);
            el.style.setProperty("--gscale", `${s}`);
            el.style.setProperty("--gouter", `${Math.round(baseH * s)}px`);
        };
        const ro = new ResizeObserver((ents) => ents.forEach((e) => apply(e.contentRect.width)));
        ro.observe(el);
        apply(el.getBoundingClientRect().width);
        return () => ro.disconnect();
    }, [baseW, baseH]);
    return ref;
}

function FitArea({ baseW, baseH, className, children }) {
    const ref = useAreaScale(baseW, baseH);
    return (
        <div
            ref={ref}
            className={className}
            style={{ width: "100%", height: "var(--gouter)", overflow: "hidden" }}
        >
            <div
                style={{
                    width: "var(--gw)",
                    height: "var(--gh)",
                    transform: "scale(var(--gscale))",
                    transformOrigin: "top left"
                }}
            >
                {children}
            </div>
        </div>
    );
}

/* ----------------- Chat Typewriter ----------------- */
function useChatPlayer(items, opts = {}) {
    const speed = opts.speed ?? 36;
    const pause = opts.pause ?? 900;
    const loop = opts.loop ?? true;
    const [rendered, setRendered] = useState([]);
    const viewportRef = useRef(null);

    useEffect(() => {
        let cancel = false;
        const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
        const jitter = () => Math.max(18, speed + Math.round((Math.random() - 0.5) * 30));
        const scrollBottom = () =>
            viewportRef.current?.scrollTo({ top: viewportRef.current.scrollHeight, behavior: "smooth" });

        setRendered([]);
        if (!items || !items.length) {
            return () => {
                cancel = true;
            };
        }

        async function play() {
            while (!cancel) {
                for (let i = 0; i < items.length && !cancel; i++) {
                    setRendered((r) => [...r, { ...items[i], text: "" }]);
                    let acc = "";
                    for (const ch of items[i].text) {
                        if (cancel) return;
                        acc += ch;
                        setRendered((r) => {
                            const next = r.slice();
                            next[next.length - 1] = { ...next[next.length - 1], text: acc };
                            return next;
                        });
                        scrollBottom();
                        await sleep(jitter());
                    }
                    await sleep(pause);
                }
                if (!loop) break;
                await sleep(1200);
                setRendered([]);
                await sleep(200);
            }
        }
        play();
        return () => {
            cancel = true;
        };
    }, [items, loop, pause, speed]);

    return { rendered, viewportRef };
}

/* ----------------- Animation Variants ----------------- */
const easeOutExpo = cubicBezier(0.16, 1, 0.3, 1);
const fadeUp = {
    hidden: { opacity: 0, y: 12 },
    show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: easeOutExpo } }
};
const staggerCol = {
    hidden: {},
    show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } }
};
const bubbleVariants = {
    hidden: { opacity: 0, y: 8, scale: 0.98 },
    show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: easeOutExpo } }
};
const cardVariants = {
    initial: { opacity: 0, y: 12, scale: 0.98 },
    animate: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.45, ease: easeOutExpo } },
    exit: { opacity: 0, y: -10, scale: 0.98, transition: { duration: 0.3, ease: easeOutExpo } }
};

/* ----------------- Phone Components ----------------- */
function WhatsAppPhoneDemo({ conversation, prompt }) {
    const phoneClass = "mockup-phone __fallback";
    const { rendered, viewportRef } = useChatPlayer(conversation || [], {
        speed: 34,
        pause: 800,
        loop: true
    });

    return (
        <FitScale baseW={280} baseH={560} className="device-float">
            <div className={phoneClass} style={{ width: 280, height: 560 }}>
                <div className="camera mockup-phone-camera" />
                <div className="display mockup-phone-display">
                    <div style={{ height: '100%', width: '100%', padding: '6px', background: "#0a1014", color: "#e5e7eb" }}>
                        <div style={{ display: 'flex', alignItems: 'center', borderTopLeftRadius: '14px', borderTopRightRadius: '14px', padding: '0 12px', height: 48, background: "#075E54", color: "white" }}>
                            <button aria-label="Back" style={{ color: 'rgba(255,255,255,0.9)', background: 'none', border: 'none', cursor: 'pointer' }}>
                                ←
                            </button>
                            <div style={{ height: 28, width: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.2)', display: 'grid', placeItems: 'center', fontSize: '12px', marginLeft: '8px' }}>
                                CB
                            </div>
                            <div style={{ marginLeft: '8px', lineHeight: 'tight' }}>
                                <div style={{ fontSize: '12px', fontWeight: '600' }}>ConvoBuddy</div>
                                <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.8)' }}>online</div>
                            </div>
                            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '12px', color: 'rgba(255,255,255,0.9)' }}>
                                <span>📹</span>
                                <span>📞</span>
                                <span>⋮</span>
                            </div>
                        </div>

                        <motion.div
                            ref={viewportRef}
                            layout
                            style={{
                                position: 'relative',
                                overflowY: 'auto',
                                padding: '8px 10px 16px',
                                height: "calc(100% - 48px - 52px)",
                                background: "radial-gradient(20% 20% at 20% 0%, rgba(255,255,255,.05), transparent 60%)",
                                scrollbarWidth: "thin"
                            }}
                        >
                            <AnimatePresence initial={false}>
                                {rendered.map((m, i) => {
                                    const isOut = m.type === "question";
                                    const bubbleStyle = isOut
                                        ? { background: "#DCF8C6", color: "#0b141a" }
                                        : { background: "#ffffff", color: "#111827" };

                                    return (
                                        <motion.div
                                            key={`${i}-${m.type}`}
                                            variants={bubbleVariants}
                                            initial="hidden"
                                            animate="show"
                                            exit="hidden"
                                            layout
                                            style={{ marginTop: '8px', display: 'flex', justifyContent: isOut ? 'flex-end' : 'flex-start' }}
                                        >
                                            <div style={{ ...bubbleStyle, maxWidth: '72%', borderRadius: '10px', padding: '6px 10px', boxShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
                                                <div style={{ fontSize: 11, lineHeight: '1.25' }}>
                                                    {m.text}
                                                </div>
                                                <div style={{ marginTop: '2px', display: 'flex', alignItems: 'center', gap: '4px', fontSize: 9, color: isOut ? "#4e6b50" : "#6b7280" }}>
                                                    <span>09:41</span>
                                                    {isOut && <span style={{ color: '#34B7F1' }}>✓✓</span>}
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </motion.div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '0 8px', borderBottomLeftRadius: '14px', borderBottomRightRadius: '14px', height: 52, background: "#121a22" }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '999px', background: '#1f2b33', padding: '8px 12px', fontSize: 12 }}>
                                <span style={{ color: 'rgba(255,255,255,0.6)' }}>😊</span>
                                <span style={{ color: 'rgba(255,255,255,0.7)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{prompt}</span>
                                <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '8px', color: 'rgba(255,255,255,0.7)' }}>
                                    <span>📎</span>
                                    <span>📷</span>
                                </div>
                            </div>
                            <button aria-label="Voice" style={{ display: 'grid', height: 36, width: 36, placeItems: 'center', borderRadius: '50%', background: "#00A884", color: "white", border: 'none', cursor: 'pointer' }}>
                                🎤
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </FitScale>
    );
}

function MelodyOrderPhone() {
    const phoneClass = "mockup-phone __fallback";
    const plans = [
        { key: 'free', name: 'Free', price: '$0/mo', desc: 'For hobby musicians', features: ['5 songs/mo', 'Basic melodies', 'Export MP3'], href: '#' },
        { key: 'pro', name: 'Pro', price: '$29/mo', desc: 'For serious creators', features: ['Unlimited songs', 'Advanced AI', 'MIDI export', 'Commercial use'], href: '#' },
        { key: 'studio', name: 'Studio', price: '$99/mo', desc: 'For professionals', features: ['Priority AI', 'Team collab', 'API access', 'White label'], href: '#' },
    ];
    const sampleLines = [
        "In the silence of the night,",
        "Stars are shining bright,",
        "Dreams take flight,",
        "To a world of endless light."
    ];

    const [idx, setIdx] = useState(1);
    const [auto, setAuto] = useState(true);
    const [typed, setTyped] = useState([]);
    const [isTyping, setIsTyping] = useState(false);

    useEffect(() => {
        if (!auto || !plans.length) return;
        const id = setInterval(() => setIdx((i) => (i + 1) % plans.length), 50000);
        return () => clearInterval(id);
    }, [auto, plans.length]);

    const pauseAuto = () => {
        setAuto(false);
        setTimeout(() => setAuto(true), 60000);
    };

    const startTyping = () => {
        if (isTyping) return;
        setIsTyping(true);
        setTyped(Array(sampleLines.length).fill(""));

        let line = 0;
        let ch = 0;
        let buf = "";
        const tId = setInterval(() => {
            buf += sampleLines[line][ch];
            setTyped((prev) => {
                const next = prev.slice();
                next[line] = buf;
                return next;
            });
            ch++;
            if (ch >= sampleLines[line].length) {
                line++;
                ch = 0;
                buf = "";
                if (line >= sampleLines.length) {
                    clearInterval(tId);
                    setIsTyping(false);
                }
            }
        }, 28);
    };

    const plan = plans[idx];
    const linesToShow = isTyping ? typed : sampleLines;

    return (
        <FitScale baseW={280} baseH={560} className="device-float">
            <div className={phoneClass} style={{ width: 280, height: 560 }}>
                <div className="camera mockup-phone-camera" />
                <div className="display mockup-phone-display">
                    <div style={{ height: '100%', width: '100%', padding: '10px 10px 8px', background: "#0f1116", color: "#e5e7eb" }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', borderRadius: '999px', padding: '4px 8px', background: "rgba(255,255,255,.09)" }}>
                                <div style={{ height: 16, width: 16, borderRadius: '4px', background: 'rgba(255,255,255,0.15)', display: 'grid', placeItems: 'center', fontSize: '9px', fontWeight: '700' }}>M</div>
                                <span style={{ fontSize: '10px' }}>🔒</span>
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '10px' }}>melodycrafters.com/order</span>
                            </div>
                        </div>

                        <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 4px' }}>
                            <div style={{ fontSize: '9px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.18em', color: "#f59e0b" }}>
                                MELODYCRAFTERS
                            </div>
                            <a href="#" style={{ fontSize: '9px', textDecoration: 'underline', color: "#fbbf24" }}>Pricing</a>
                        </div>

                        <div style={{ marginTop: '8px', overflowX: 'auto' }}>
                            <div style={{ display: 'flex', gap: '4px', minWidth: 'max-content', padding: '0 4px' }}>
                                {plans.map((p, i) => (
                                    <button
                                        key={p.key}
                                        onClick={() => { setIdx(i); pauseAuto(); }}
                                        style={{
                                            borderRadius: '999px',
                                            padding: '4px 8px',
                                            fontSize: '10px',
                                            whiteSpace: 'nowrap',
                                            background: i === idx ? "#f97316" : "rgba(255,255,255,.06)",
                                            color: i === idx ? "#fff" : "rgba(255,255,255,.9)",
                                            border: i === idx ? "1px solid rgba(255,255,255,.18)" : "1px solid rgba(255,255,255,.08)",
                                            cursor: 'pointer'
                                        }}
                                    >
                                        {p.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div style={{ marginTop: '8px', position: 'relative', overflow: 'hidden', borderRadius: '12px', background: "rgba(255,255,255,.03)" }}>
                            <div style={{ padding: '12px', minHeight: '300px' }}>
                                <AnimatePresence mode="popLayout" initial={false}>
                                    <motion.div
                                        key={plan.key}
                                        variants={cardVariants}
                                        initial="initial"
                                        animate="animate"
                                        exit="exit"
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <div style={{ fontSize: '13px', fontWeight: '600' }}>{plan.name}</div>
                                            <div style={{ fontSize: '12px', color: "#fbbf24" }}>{plan.price}</div>
                                        </div>
                                        <div style={{ marginTop: '4px', fontSize: '11px', opacity: 0.85 }}>{plan.desc}</div>
                                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                            {plan.features.map((f) => (
                                                <div key={f} style={{ borderRadius: '999px', padding: '2px 8px', fontSize: '10px', background: "rgba(255,255,255,.06)" }}>
                                                    {f}
                                                </div>
                                            ))}
                                        </div>

                                        <div style={{ marginTop: '12px', display: 'grid', gap: '8px' }}>
                                            <label style={{ fontSize: '10px', opacity: 0.7 }}>Your Story</label>
                                            <textarea
                                                rows={2}
                                                defaultValue="On our first date, we watched the stars..."
                                                style={{ width: '100%', borderRadius: '8px', padding: '10px', fontSize: '11px', outline: 'none', background: "rgba(255,255,255,.06)", color: '#fff', border: 'none' }}
                                            />

                                            <div style={{ borderRadius: '8px', padding: '8px', background: "rgba(255,255,255,.06)" }}>
                                                <div style={{ fontSize: '10px', fontWeight: '600', color: "#fbbf24" }}>
                                                    Generated Lyrics
                                                </div>
                                                <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px', fontSize: '11px', lineHeight: 1.3 }}>
                                                    {linesToShow.map((l, i) => (
                                                        <p key={i} style={{ opacity: 0.9 }}>{l}</p>
                                                    ))}
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <button
                                                    onClick={() => { startTyping(); pauseAuto(); }}
                                                    style={{ borderRadius: '999px', padding: '4px 12px', fontSize: '11px', fontWeight: '600', color: 'white', background: "#f97316", border: 'none', cursor: 'pointer' }}
                                                >
                                                    {isTyping ? "Generating..." : "Generate Lyrics"}
                                                </button>
                                                <a
                                                    href={plan.href}
                                                    style={{ borderRadius: '999px', padding: '4px 12px', fontSize: '11px', fontWeight: '600', color: 'white', background: "#fb923c", textDecoration: 'none' }}
                                                >
                                                    Pricing
                                                </a>
                                            </div>
                                        </div>
                                    </motion.div>
                                </AnimatePresence>
                            </div>

                            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: '3px', background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
                                <div
                                    key={`${idx}-${auto ? "auto" : "manual"}`}
                                    style={{
                                        height: '100%',
                                        width: '0%',
                                        background: "linear-gradient(90deg,#fb923c,#fbbf24)",
                                        animation: auto ? "mcProgress 50s linear forwards" : undefined
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </FitScale>
    );
}

function HappyFitAIPhones() {
    const c = {
        deep: "#103731",
        header: "#143F36",
        cream: "#F4F7EE",
        card: "#FFFFFF",
        chip: "#E8F1E6",
        text: "#1F2B28",
        muted: "#5F716A",
        ring1: "#8DD79A",
        border: "#E7EDE2"
    };

    const phoneClass = "mockup-phone __fallback";
    const profile = {
        name: "Sarah",
        steps: 6847,
        goal: 10000,
        calories: 245,
        kms: 4.2
    };

    const pct = Math.max(0, Math.min(1, profile.steps / profile.goal));
    const deg = Math.round(pct * 360);

    const HomePhone = () => (
        <div className={phoneClass} style={{ width: 280, height: 560 }}>
            <div className="mockup-phone-camera" />
            <div className="mockup-phone-display">
                <div style={{ height: '100%', width: '100%', background: c.cream, color: c.text }}>
                    <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: c.deep, color: "white" }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>☰</span>
                            <span style={{ fontSize: '11px' }}>Dec 12, 2024</span>
                        </div>
                        <span>🔔</span>
                    </div>

                    <div style={{ padding: '8px 16px' }}>
                        <div style={{ fontSize: '16px', lineHeight: 'tight' }}>
                            <span style={{ color: c.text }}>Good morning, {profile.name}! 👋</span>
                            <div style={{ fontWeight: '600' }}>Let's crush your goals today</div>
                        </div>

                        <div style={{ marginTop: '12px', borderRadius: '16px', padding: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', background: c.card, border: `1px solid ${c.border}` }}>
                            <div style={{ margin: '0 auto', position: 'relative', width: 150, height: 150 }}>
                                <div
                                    style={{
                                        position: 'absolute',
                                        inset: 0,
                                        borderRadius: '50%',
                                        background: `conic-gradient(${c.ring1} ${deg}deg, rgba(0,0,0,0.06) 0)`
                                    }}
                                />
                                <div
                                    style={{ position: 'absolute', inset: 16, borderRadius: '50%', display: 'grid', placeContent: 'center', textAlign: 'center', background: c.cream, boxShadow: `inset 0 0 0 1px ${c.border}` }}
                                >
                                    <div style={{ fontSize: '16px', fontWeight: '700', color: c.text }}>
                                        {profile.steps}
                                    </div>
                                    <div style={{ fontSize: '11px', color: c.muted }}>
                                        / {profile.goal} steps
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', fontSize: '12px', color: c.muted }}>
                                <div>🔥 {profile.calories} Cal</div>
                                <div>🧭 {profile.kms} kms</div>
                                <div>⚖️ BMR</div>
                            </div>
                        </div>

                        <div style={{ marginTop: '12px', fontSize: '12px', fontWeight: '500', color: c.text }}>
                            Activity Dashboard
                        </div>
                        <div style={{ marginTop: '8px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                            {[
                                { k: 'Steps', v: '6.8k', sub: 'Today' },
                                { k: 'Workout', v: '25m', sub: 'This week' },
                                { k: 'Weight', v: '—', sub: 'Not tracked' }
                            ].map((stat) => (
                                <div key={stat.k} style={{ borderRadius: '12px', textAlign: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', background: c.card, border: `1px solid ${c.border}`, padding: '10px 6px' }}>
                                    <div style={{ fontSize: '11px', color: c.muted }}>{stat.k}</div>
                                    <div style={{ fontSize: '16px', fontWeight: '600', color: c.text }}>{stat.v}</div>
                                    <div style={{ fontSize: '11px', color: c.muted }}>{stat.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const SummaryPhone = () => (
        <div className={phoneClass} style={{ width: 280, height: 560 }}>
            <div className="mockup-phone-camera" />
            <div className="mockup-phone-display">
                <div style={{ height: '100%', width: '100%', background: c.cream, color: c.text }}>
                    <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: '12px', background: c.header, color: "white" }}>
                        <span>←</span>
                        <div style={{ fontWeight: '600' }}>Workout Summary</div>
                    </div>

                    <div style={{ padding: '12px 16px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                            {[
                                { label: 'Correct', value: '08' },
                                { label: 'Incorrect', value: '02' }
                            ].map((b) => (
                                <div key={b.label} style={{ borderRadius: '16px', padding: '12px', textAlign: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', background: c.card, border: `1px solid ${c.border}` }}>
                                    <div style={{ fontSize: '12px', color: c.muted }}>{b.label}</div>
                                    <div style={{ fontSize: '18px', fontWeight: '700', color: c.text }}>{b.value}</div>
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: '12px', borderRadius: '16px', padding: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', background: c.card, border: `1px solid ${c.border}` }}>
                            <div style={{ textAlign: 'center' }}>
                                <div style={{ fontSize: '13px' }}>Full Body Workout</div>
                                <div style={{ fontSize: '12px', color: c.muted }}>
                                    Duration: <span style={{ fontWeight: '600', color: c.text }}>25 min</span>
                                </div>
                            </div>

                            <div style={{ marginTop: '12px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', alignItems: 'end', gap: '12px', height: '92px' }}>
                                <div style={{ borderRadius: '4px', height: '60px', background: "#FF6B6B" }} />
                                <div style={{ borderRadius: '4px', height: '82px', background: "#3B82F6" }} />
                                <div style={{ borderRadius: '4px', height: '45px', background: "#F59E0B" }} />
                            </div>
                            <div style={{ marginTop: '4px', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', textAlign: 'center', fontSize: '11px', color: c.muted }}>
                                <div>Wrong</div>
                                <div>Beginner</div>
                                <div>Intermediate</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const TodayPhone = () => (
        <div className={phoneClass} style={{ width: 280, height: 560 }}>
            <div className="mockup-phone-camera" />
            <div className="mockup-phone-display">
                <div style={{ height: '100%', width: '100%', background: c.cream, color: c.text }}>
                    <div style={{ padding: '12px 16px 8px', display: 'flex', alignItems: 'center', gap: '12px', background: c.header, color: "white" }}>
                        <span>←</span>
                        <div style={{ fontWeight: '600' }}>Today's Plan</div>
                    </div>

                    <div style={{ padding: '12px 16px' }}>
                        <div style={{ borderRadius: '16px', padding: '8px', display: 'flex', alignItems: 'center', gap: '8px', overflowX: 'auto', background: c.header }}>
                            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map((d, i) => (
                                <div
                                    key={d}
                                    style={{
                                        borderRadius: '999px',
                                        padding: '4px 12px',
                                        fontSize: '12px',
                                        whiteSpace: 'nowrap',
                                        background: i === 2 ? '#A5D8A6' : "rgba(255,255,255,.12)",
                                        color: i === 2 ? c.text : "white",
                                        fontWeight: i === 2 ? 700 : 500
                                    }}
                                >
                                    {d}
                                </div>
                            ))}
                        </div>

                        <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            {[
                                { t: 'Warm-up Stretches', tag: 'Warmup', sets: '2x10' },
                                { t: 'Push-ups', tag: 'Core Exercise', sets: '3x15' },
                                { t: 'Cool Down', tag: 'Cool Down', sets: '1x5 min' }
                            ].map((it) => (
                                <div
                                    key={it.t}
                                    style={{
                                        borderRadius: '16px',
                                        padding: '12px',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                        background: c.card,
                                        border: `1px solid ${c.border}`,
                                        position: 'relative',
                                        overflow: 'hidden'
                                    }}
                                >
                                    <div
                                        style={{
                                            position: 'absolute',
                                            right: -20,
                                            top: -10,
                                            width: 140,
                                            height: 140,
                                            background: c.chip,
                                            borderRadius: 120
                                        }}
                                    />
                                    <div style={{ position: 'relative' }}>
                                        <div style={{ fontSize: '13px', fontWeight: '600' }}>
                                            {it.t} <span style={{ opacity: 0.7 }}>({it.tag})</span>
                                        </div>
                                        <div style={{ marginTop: '4px', fontSize: '12px', color: c.muted }}>
                                            Sets: {it.sets}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <FitArea baseW={980} baseH={620} className="overflow-hidden relative">
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, height: '240px', borderBottomLeftRadius: '18px', borderBottomRightRadius: '18px', background: c.deep }} />
            <div style={{ position: 'absolute', left: 70, top: 120, transform: "scale(0.94)" }}>
                <HomePhone />
            </div>
            <div style={{ position: 'absolute', left: 350, top: 64, transform: "scale(1)" }}>
                <SummaryPhone />
            </div>
            <div style={{ position: 'absolute', left: 640, top: 98, transform: "scale(0.92)" }}>
                <TodayPhone />
            </div>
        </FitArea>
    );
}

function RideWithDashScreens() {
    const { t } = useLanguage();
    const ui = t('products.rideWithDash.ui') || {};
    const vehicleCards = ui.vehicles || [];
    const files = ui.files || [];

    const c = {
        ink: '#0F172A',
        muted: '#64748B',
        line: '#DBEAFE',
        soft: '#F8FBFF',
        blue: '#8DBBE8',
        deep: '#111827',
        red: '#EF4444'
    };

    const Panel = ({ title, kicker, children }) => (
        <div style={{
            background: '#FFFFFF',
            border: `1px solid ${c.line}`,
            borderRadius: 24,
            boxShadow: '0 24px 60px rgba(15, 23, 42, 0.12)',
            overflow: 'hidden',
            minHeight: 430
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '18px 20px', borderBottom: '1px solid #E5E7EB', background: c.soft }}>
                <div>
                    <div style={{ fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase', color: c.blue, fontWeight: 800 }}>{kicker}</div>
                    <div style={{ marginTop: 4, color: c.ink, fontWeight: 800, fontSize: 20 }}>{title}</div>
                </div>
                <div style={{ display: 'grid', placeItems: 'center', width: 36, height: 36, borderRadius: 12, background: '#111827', color: '#FFFFFF', fontWeight: 900 }}>D</div>
            </div>
            {children}
        </div>
    );

    return (
        <FitArea baseW={1020} baseH={600} className="overflow-hidden relative">
            <div style={{ position: 'absolute', inset: '24px 0 0 0', borderRadius: 34, background: 'linear-gradient(135deg, #EFF6FF, #FFFFFF 48%, #F8FAFC)', border: '1px solid #E5E7EB' }} />
            <div style={{ position: 'absolute', left: 26, top: 58, width: 294, transform: 'rotate(-1deg)' }}>
                <Panel title={ui.chatTitle || 'Dash chat'} kicker={ui.chatKicker || 'WhatsApp + web'}>
                    <div style={{ padding: 18, color: c.ink }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
                            <span style={{ width: 8, height: 8, borderRadius: 999, background: c.blue }} />
                            <span style={{ fontSize: 13, color: c.muted, fontWeight: 700 }}>{ui.chatMeta || 'Verified WhatsApp identity'}</span>
                        </div>
                        <div style={{ borderRadius: 18, background: '#F1F5F9', padding: 14, fontSize: 13, lineHeight: 1.55 }}>
                            <strong>{ui.userBubble || 'My car clicks but will not start.'}</strong>
                        </div>
                        <div style={{ marginTop: 12, borderRadius: 18, background: '#FFFFFF', border: '1px solid #E5E7EB', padding: 14, fontSize: 12.5, lineHeight: 1.55, color: c.muted }}>
                            <strong style={{ color: c.ink }}>{ui.answerTitle || 'Dash diagnostic'}</strong>
                            <div>{ui.answer || 'Check battery strength, starter click pattern, and dim headlights before adding this to your work plan.'}</div>
                        </div>
                        <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #FECACA', borderRadius: 16, padding: 12, color: c.red, fontSize: 12, fontWeight: 800 }}>
                            <span>▶</span>
                            <span>{ui.video || 'Watch diagnostic video'}</span>
                        </div>
                        <div style={{ marginTop: 18, borderRadius: 999, background: '#F8FAFC', border: '1px solid #E5E7EB', padding: '10px 14px', color: c.muted, fontSize: 12 }}>
                            {ui.prompt || 'Give Dash a photo or ask a question...'}
                        </div>
                    </div>
                </Panel>
            </div>
            <div style={{ position: 'absolute', left: 362, top: 26, width: 294 }}>
                <Panel title={ui.garageTitle || 'Your Collection'} kicker={ui.garageKicker || 'Member garage'}>
                    <div style={{ padding: 18 }}>
                        {vehicleCards.slice(0, 3).map((v, i) => (
                            <div key={`${v.name}-${i}`} style={{ border: `1px solid ${c.line}`, borderRadius: 18, padding: 14, marginBottom: 12, background: '#FFFFFF' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                    <div style={{ display: 'grid', placeItems: 'center', width: 40, height: 40, borderRadius: 12, background: '#EFF6FF' }}>🚗</div>
                                    <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.16em', color: c.muted, fontWeight: 800 }}>{v.plans || '3 plans'}</span>
                                </div>
                                <div style={{ marginTop: 10, color: c.ink, fontSize: 18, fontWeight: 900 }}>{v.name}</div>
                                <div style={{ color: c.muted, fontSize: 13 }}>{v.model}</div>
                                <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, fontSize: 11, color: c.muted }}>
                                    <span>{ui.vinStatus || 'VIN status'}</span><strong style={{ color: c.ink }}>{v.vin || 'Missing'}</strong>
                                    <span>{ui.reliability || 'Reliability'}</span><strong style={{ color: c.ink }}>100/10</strong>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>
            <div style={{ position: 'absolute', right: 26, top: 58, width: 294, transform: 'rotate(1deg)' }}>
                <Panel title={ui.gloveboxTitle || 'Digital Glovebox'} kicker={ui.gloveboxKicker || 'Files + AI reports'}>
                    <div style={{ padding: 18 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}>
                            {(ui.tabs || ['Uploaded Files', 'AI Reports']).map((tab, i) => (
                                <div key={tab} style={{ borderRadius: 999, background: i === 0 ? '#111827' : '#F1F5F9', color: i === 0 ? '#FFFFFF' : c.muted, textAlign: 'center', padding: '8px 10px', fontSize: 11, fontWeight: 800 }}>{tab}</div>
                            ))}
                        </div>
                        {files.slice(0, 3).map((f) => (
                            <div key={f} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, border: '1px solid #E5E7EB', borderRadius: 14, padding: 11, marginBottom: 8, fontSize: 12, color: c.ink, fontWeight: 800 }}>
                                <span>📄 {f}</span>
                                <span style={{ color: c.muted }}>↗</span>
                            </div>
                        ))}
                        <div style={{ marginTop: 14, borderRadius: 18, border: '1px solid #E5E7EB', overflow: 'hidden', background: '#FFFFFF' }}>
                            <div style={{ background: '#111827', color: '#FFFFFF', padding: '10px 12px', fontSize: 12, fontWeight: 900 }}>{ui.reportTitle || 'Vehicle report'}</div>
                            <div style={{ padding: 12 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                                    <strong style={{ color: c.ink }}>{ui.reportVehicle || '2019 Ford Fiesta'}</strong>
                                    <span style={{ color: c.red, fontWeight: 900, fontSize: 11 }}>{ui.reportStatus || 'Needs maintenance'}</span>
                                </div>
                                <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: 'linear-gradient(90deg, #22C55E 25%, #FACC15 55%, #EF4444 100%)' }} />
                                <div style={{ marginTop: 10, color: c.muted, fontSize: 11 }}>{ui.reportSummary || 'Open diagnostics, work plan, service history, and parts in one place.'}</div>
                            </div>
                        </div>
                    </div>
                </Panel>
            </div>
        </FitArea>
    );
}

/* ----------------- Main Products Component ----------------- */
export default function Products() {
    const { t } = useLanguage();
    const hydrated = useIdleHydration(160);
    const r1 = useReveal();
    const r2 = useReveal();
    const r3 = useReveal();
    const r4 = useReveal();

    const convobuddyConversation = [
        { type: "question", text: t('products.convoBuddy.demo.q1') },
        { type: "answer", text: t('products.convoBuddy.demo.a1') },
        { type: "question", text: t('products.convoBuddy.demo.q2') },
        { type: "answer", text: t('products.convoBuddy.demo.a2') }
    ];

    const styles = {
        section: {
            background: 'white',
            paddingBottom: '80px'
        },
        container: {
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '0 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '64px'
        },
        hero: {
            background: 'linear-gradient(to bottom, white, #f9f9ff)',
            padding: '40px 0'
        },
        heroInner: {
            maxWidth: '1100px',
            margin: '0 auto',
            padding: '0 16px',
            textAlign: 'center'
        },
        h1: {
            fontSize: '36px',
            fontWeight: '700',
            color: '#111827',
            marginBottom: '12px'
        },
        heroDesc: {
            maxWidth: '768px',
            margin: '0 auto',
            fontSize: '16px',
            color: '#5c5c5c'
        },
        article: {
            display: 'grid',
            gap: '40px',
            gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr',
            alignItems: 'start',
            justifyItems: 'center'
        }
    };

    return (
        <>
            <style>{`
        @keyframes floaty { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
        @keyframes mcProgress { from { width: 0% } to { width: 100% } }
        .device-float { animation: floaty 8s ease-in-out infinite; }
        .mockup-phone.__fallback { position: relative; width: 260px; aspect-ratio: 9/16; border: 12px solid #111; border-radius: 2rem; background:#000; box-shadow: 0 20px 70px rgba(0,0,0,.25); }
        .mockup-phone.__fallback .mockup-phone-camera,
        .mockup-phone.__fallback .camera { position:absolute; top:8px; left:50%; transform:translateX(-50%); width:90px; height:14px; background:#111; border-radius:999px; }
        .mockup-phone.__fallback .mockup-phone-display,
        .mockup-phone.__fallback .display { position:absolute; inset:12px; border-radius:1.6rem; overflow:hidden; background:#111; color:#fff; }
      `}</style>

            <section style={styles.hero}>
                <div style={styles.heroInner}>
                    <h1 style={styles.h1}>{t('products.title')}</h1>
                    <p style={styles.heroDesc}>
                        {t('products.description')}
                    </p>
                </div>
            </section>

            <section style={styles.section} id="products">
                <div style={styles.container}>
                    {/* ConvoBuddy */}
                    <article ref={r1} style={styles.article}>
                        <motion.div
                            variants={staggerCol}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.25 }}
                            style={{ width: '100%', maxWidth: '640px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            <motion.span
                                variants={fadeUp}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.26em', color: '#3B63F0' }}
                            >
                                <span style={{ height: 8, width: 8, borderRadius: '50%', background: '#3B63F0' }} />
                                {t('products.convoBuddy.tag')}
                            </motion.span>
                            <motion.h2 variants={fadeUp} style={{ fontSize: '24px', fontWeight: '600', color: '#222' }}>
                                {t('products.convoBuddy.title')}
                            </motion.h2>
                            <motion.p variants={fadeUp} style={{ fontSize: '15px', lineHeight: '1.7', color: '#565656' }}>
                                {t('products.convoBuddy.description1')}
                            </motion.p>
                            <motion.p variants={fadeUp} style={{ fontSize: '15px', lineHeight: '1.7', color: '#565656' }}>
                                {t('products.convoBuddy.description2')}
                            </motion.p>
                            <motion.ul variants={staggerCol} style={{ marginLeft: '16px', marginTop: '4px', listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '15px', color: '#565656' }}>
                                {(t('products.convoBuddy.list') || []).map((b, i) => (
                                    <motion.li key={i} variants={fadeUp}>{b}</motion.li>
                                ))}
                            </motion.ul>
                            <motion.a
                                variants={fadeUp}
                                href="https://www.convobuddy.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: 0, fontSize: '14px', fontWeight: '600', color: '#3B63F0', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                                {t('products.convoBuddy.link')}
                            </motion.a>
                        </motion.div>

                        <div style={{ width: '100%', display: 'grid', placeItems: 'center' }}>
                            {!hydrated ? (
                                <div style={{ width: '280px', height: '560px', borderRadius: '34px', background: 'rgba(0,0,0,0.2)' }} />
                            ) : (
                                <WhatsAppPhoneDemo
                                    conversation={convobuddyConversation}
                                    prompt="Type a message..."
                                />
                            )}
                        </div>
                    </article>

                    {/* MelodyCrafters */}
                    <article ref={r2} style={{ ...styles.article, gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr' }}>
                        <div style={{ width: '100%', display: 'grid', placeItems: 'center', order: window.innerWidth >= 1024 ? 1 : 2 }}>
                            {!hydrated ? (
                                <div style={{ width: '280px', height: '560px', borderRadius: '34px', background: 'rgba(0,0,0,0.4)' }} />
                            ) : (
                                <MelodyOrderPhone />
                            )}
                        </div>

                        <motion.div
                            variants={staggerCol}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.25 }}
                            style={{ width: '100%', maxWidth: '650px', display: 'flex', flexDirection: 'column', gap: '16px', order: window.innerWidth >= 1024 ? 2 : 1 }}
                        >
                            <motion.span
                                variants={fadeUp}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.26em', color: '#F5861F' }}
                            >
                                <span style={{ height: 8, width: 8, borderRadius: '50%', background: '#F5861F' }} />
                                {t('products.melodyCrafters.tag')}
                            </motion.span>
                            <motion.h2 variants={fadeUp} style={{ fontSize: '20px', fontWeight: '600', color: '#222222' }}>
                                {t('products.melodyCrafters.title')}
                            </motion.h2>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555' }}>
                                {t('products.melodyCrafters.description1')}
                            </motion.p>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555' }}>
                                {t('products.melodyCrafters.description2')}
                            </motion.p>
                            <motion.ul variants={staggerCol} style={{ marginLeft: '16px', marginTop: '4px', listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', color: '#555555' }}>
                                {(t('products.melodyCrafters.list') || []).map((b, i) => (
                                    <motion.li key={i} variants={fadeUp}>{b}</motion.li>
                                ))}
                            </motion.ul>
                            <motion.a
                                variants={fadeUp}
                                href="https://melodycrafters.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: 0, fontSize: '12px', fontWeight: '600', color: '#F5861F', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                                {t('products.melodyCrafters.link')}
                            </motion.a>
                        </motion.div>
                    </article>

                    {/* HappyFit */}
                    <article ref={r3} style={styles.article}>
                        <motion.div
                            variants={staggerCol}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.25 }}
                            style={{ width: '100%', maxWidth: '650px', display: 'flex', flexDirection: 'column', gap: '16px' }}
                        >
                            <motion.span
                                variants={fadeUp}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.26em', color: '#04B4AE' }}
                            >
                                <span style={{ height: 8, width: 8, borderRadius: '50%', background: '#7CD845' }} />
                                {t('products.happyFit.tag')}
                            </motion.span>
                            <motion.h2 variants={fadeUp} style={{ fontSize: '20px', fontWeight: '600', color: '#222222' }}>
                                {t('products.happyFit.title')}
                            </motion.h2>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555' }}>
                                {t('products.happyFit.description1')}
                            </motion.p>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555' }}>
                                {t('products.happyFit.description2')}
                            </motion.p>
                            <motion.ul variants={staggerCol} style={{ marginLeft: '16px', marginTop: '4px', listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', color: '#555555' }}>
                                {(t('products.happyFit.list') || []).map((b, i) => (
                                    <motion.li key={i} variants={fadeUp}>{b}</motion.li>
                                ))}
                            </motion.ul>
                            <motion.a
                                variants={fadeUp}
                                href="https://happyfit.ai/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: 0, fontSize: '12px', fontWeight: '600', color: '#04B4AE', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                                {t('products.happyFit.link')}
                            </motion.a>
                        </motion.div>

                        <div style={{ width: '100%', display: 'grid', placeItems: 'center' }}>
                            {!hydrated ? (
                                <div style={{ width: '100%', maxWidth: '980px', height: '480px', borderRadius: '34px', background: 'rgba(16, 185, 129, 0.2)' }} />
                            ) : (
                                <HappyFitAIPhones />
                            )}
                        </div>
                    </article>

                    {/* RideWithDash */}
                    <article ref={r4} style={{ ...styles.article, gridTemplateColumns: window.innerWidth >= 1024 ? '1fr 1fr' : '1fr' }}>
                        <div style={{ width: '100%', display: 'grid', placeItems: 'center', order: window.innerWidth >= 1024 ? 1 : 2 }}>
                            {!hydrated ? (
                                <div style={{ width: '100%', maxWidth: '980px', height: '480px', borderRadius: '34px', background: 'rgba(141, 187, 232, 0.22)' }} />
                            ) : (
                                <RideWithDashScreens />
                            )}
                        </div>

                        <motion.div
                            variants={staggerCol}
                            initial="hidden"
                            whileInView="show"
                            viewport={{ once: true, amount: 0.25 }}
                            style={{ width: '100%', maxWidth: '650px', display: 'flex', flexDirection: 'column', gap: '16px', order: window.innerWidth >= 1024 ? 2 : 1 }}
                        >
                            <motion.span
                                variants={fadeUp}
                                style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '10px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.26em', color: '#111827' }}
                            >
                                <span style={{ height: 8, width: 8, borderRadius: '50%', background: '#8DBBE8' }} />
                                {t('products.rideWithDash.tag')}
                            </motion.span>
                            <motion.h2 variants={fadeUp} style={{ fontSize: '20px', fontWeight: '600', color: '#222222' }}>
                                {t('products.rideWithDash.title')}
                            </motion.h2>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555', lineHeight: 1.7 }}>
                                {t('products.rideWithDash.description1')}
                            </motion.p>
                            <motion.p variants={fadeUp} style={{ fontSize: '14px', color: '#555555', lineHeight: 1.7 }}>
                                {t('products.rideWithDash.description2')}
                            </motion.p>
                            <motion.ul variants={staggerCol} style={{ marginLeft: '16px', marginTop: '4px', listStyle: 'disc', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px', color: '#555555' }}>
                                {(t('products.rideWithDash.list') || []).map((b, i) => (
                                    <motion.li key={i} variants={fadeUp}>{b}</motion.li>
                                ))}
                            </motion.ul>
                            <motion.a
                                variants={fadeUp}
                                href="https://ridewithdash.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ padding: 0, fontSize: '12px', fontWeight: '600', color: '#111827', textDecoration: 'none' }}
                                onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
                                onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
                            >
                                {t('products.rideWithDash.link')}
                            </motion.a>
                        </motion.div>
                    </article>
                </div>
            </section>
        </>
    );
}
